/* eslint-disable jsx-a11y/anchor-is-valid */
import { useIonModal, useIonPopover } from '@ionic/react';
import classNames from 'classnames';
import { AllowedBusinessTypes, Backup, Business, BusinessServiceLookup, DeliveryType, EmptyBusiness } from 'model';
import React, { useEffect } from 'react';
import {
	useAddBusinessBannerMutation,
	useAddBusinessLogoMutation,
	useLazyFillBusinessDataQuery,
	useRemoveBusinessBannerMutation,
	useRemoveBusinessLogoMutation
} from '../api/BusinessApi';
import { BusinessWithSimpleItems } from '../formConfigs/EditBusinessFieldsConfig';
import { FormValuesType, SetValuesFunction } from '../hooks/useFormValues';

// eslint-disable-next-line css-modules/no-unused-class
import { useGetBackupsForDocumentQuery } from '../api/BackupApi';
import { useLoading } from '../hooks/useQuirkLoading';
import { BackupsList } from './BackupList';
import styles from './BusinessEditFields.module.css';
import { BusinessImageLookup } from './BusinessImageLookup';
import { FormCheckbox } from './Forms/FormCheckbox';
import { FormImageUpload } from './Forms/FormImageUpload';
import { FormInput } from './Forms/FormInput';
import { FormSelectBox } from './Forms/FormSelectBox';
import { FormTextarea } from './Forms/FormTextarea';
import { IconButton } from './IconButton';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from './Forms/FormFields.module.css';
import { IconAddCircle, IconCamera, IconDownload, IconTrash } from './QuirkIcons';

const RESTORE_KEYS: Array<keyof Business> = [
	'name',
	'address',
	'description',
	'city',
	'district',
	'services',
	'status',
	'delivery',
	'patio',
	'hours',
	'phone',
	'website',
	'email',
	'logoURL',
	'logoBackgroundColor',
	'logo',
	'banner',
	'bannerURL',
	'items',
	'delivery',
	'patio',
	'otherDelivery',
	'businessType'
];

export const BusinessEditFields: React.FC<{
	setValues: SetValuesFunction<BusinessWithSimpleItems>;
	formValues: FormValuesType<BusinessWithSimpleItems>;
	onSubmit: () => Promise<void>;
	business?: BusinessWithSimpleItems | null;
}> = ({ setValues, formValues, business }) => {
	const [trigger, fillBusinessResults] = useLazyFillBusinessDataQuery();
	useLoading({ message: 'Getting data from website...', isLoading: fillBusinessResults.isLoading });
	useEffect(() => {
		const { data, isLoading, isError, isFetching } = fillBusinessResults;
		if (data && !isLoading && !isError && !isFetching) {
			setValues(current => {
				const { name, description, phone, hours, address } = data;
				return {
					name: current.name?.value || name,
					description: current.description?.value || description,
					phone: current.phone?.value || phone,
					hours: current.hours?.value || hours,
					address: current.address?.value || address
				};
			});
		}
	}, [fillBusinessResults, setValues]);

	const [present, dismiss] = useIonModal(BusinessImageLookup, {
		selectImage: (image: string) => {
			setValues({ logoURL: image });
			dismiss();
		},
		cancel: () => {
			dismiss();
		},
		business: formValues
	});
	const [presentBanner, dismissBanner] = useIonModal(BusinessImageLookup, {
		selectImage: (image: string) => {
			setValues({ bannerURL: image });
			dismissBanner();
		},
		cancel: () => {
			dismissBanner();
		},
		business: formValues
	});

	const id = formValues._id?.value;

	const { data: backups } = useGetBackupsForDocumentQuery({ id: id ?? '' }, { skip: id === undefined });

	const handleRestoreClick = (backup: Backup<Business>) => {
		for (const key of RESTORE_KEYS) {
			setValues({ [key]: backup.document[key] ?? '' });
		}
		hideBackups();
	};

	const [showBackups, hideBackups] = useIonPopover(
		<div className={styles.backupsPopover}>
			<BackupsList backups={backups ?? []} onSelectBackup={handleRestoreClick} />
		</div>
	);

	return (
		<>
			<div className={formStyles.fieldsContainer}>
				{formValues && (
					<>
						{backups !== undefined && backups.length > 0 && (
							<div className={styles.imageFetchButton}>
								<IconButton
									label="Restore data from previous revision"
									icon={<IconDownload />}
									onClick={event => showBackups({ event })}
								/>
							</div>
						)}
						<FormInput id="website" setValues={setValues} field={formValues.website} label="Website" />
						<div className={styles.imageFetchButton}>
							<IconButton
								label="Fill data from website"
								icon={<IconAddCircle />}
								disabled={!formValues.website?.value || formValues.website?.value === ''}
								onClick={() => trigger({ ...EmptyBusiness, website: formValues.website?.value || '' })}
							/>
						</div>
						<FormInput id="name" setValues={setValues} field={formValues.name} label="Name" />
						<FormTextarea
							id="description"
							setValues={setValues}
							field={formValues.description}
							label="Description"
						/>
						<FormSelectBox
							id="businessType"
							setValues={setValues}
							field={formValues.businessType}
							label="Business Type"
							options={AllowedBusinessTypes}
						/>

						<div
							className={classNames(
								formValues?.logoBackgroundColor?.value === ''
									? styles.backgroundNotVisible
									: styles.backgroundVisible
							)}
						>
							<FormInput
								id="logoBackgroundColor"
								type="color"
								setValues={setValues}
								field={formValues.logoBackgroundColor}
								label="Background color for logo"
							/>
							<div className={styles.removeLogoBackgroundColor}>
								<IconButton
									label="Remove Logo background color"
									icon={<IconTrash />}
									onClick={() => setValues({ logoBackgroundColor: '' })}
								/>
							</div>
						</div>

						<div
							className={classNames(
								styles.addLogoBackgroundColor,
								formValues?.logoBackgroundColor?.value !== ''
									? styles.backgroundNotVisible
									: styles.backgroundVisible
							)}
						>
							<IconButton
								label={'Add Logo background color'}
								icon={<IconAddCircle />}
								onClick={() => setValues({ logoBackgroundColor: '#ffffff' })}
							/>
						</div>

						{business?.logo === '' && (
							<>
								<FormInput
									id="logoURL"
									setValues={setValues}
									field={formValues.logoURL}
									label="Logo Image URL"
								/>
								{formValues.website?.value && formValues.website?.value !== '' && (
									<div className={styles.imageFetchButton}>
										<IconButton
											label="Get Images from website"
											icon={<IconCamera />}
											onClick={() => present()}
										/>
									</div>
								)}
							</>
						)}
						{business?.banner === '' && (
							<>
								<FormInput
									id="bannerURL"
									setValues={setValues}
									field={formValues.bannerURL}
									label="Banner Image URL"
								/>
								{formValues.website?.value && formValues.website?.value !== '' && (
									<div className={styles.imageFetchButton}>
										<IconButton
											label="Get Images from website"
											icon={<IconCamera />}
											onClick={() => presentBanner()}
										/>
									</div>
								)}
							</>
						)}
						{business && (
							<>
								<FormImageUpload
									url={`api/business/image/${business._id}/`}
									fieldName="logo"
									document={business}
									setValues={setValues}
									label="Logo Image"
									removeImage={useRemoveBusinessLogoMutation}
									addImage={useAddBusinessLogoMutation}
								/>

								<FormImageUpload
									url={`api/business/image/${business._id}/`}
									fieldName="banner"
									document={business}
									setValues={setValues}
									label="Banner Image"
									removeImage={useRemoveBusinessBannerMutation}
									addImage={useAddBusinessBannerMutation}
								/>
							</>
						)}

						<FormInput
							id="itemString"
							setValues={setValues}
							field={formValues.itemString}
							label="Items for sale or service"
						/>
						<hr />
						<div className="space20" />
						<FormInput id="address" setValues={setValues} field={formValues.address} label="Address" />
						<FormInput id="district" setValues={setValues} field={formValues.district} label="District" />
						<FormInput id="city" setValues={setValues} field={formValues.city} label="City" />
						<FormInput id="phone" setValues={setValues} field={formValues.phone} label="Phone" />
						<FormInput id="email" setValues={setValues} field={formValues.email} label="Email" />
						<FormInput id="hours" setValues={setValues} field={formValues.hours} label="Hours" />
						<hr />
						<div className="space20" />
						<FormSelectBox
							id="status"
							setValues={setValues}
							field={formValues.status}
							label="Status"
							options={{ open: 'Open', online: 'Online', closed: 'Closed' }}
						/>
						<FormCheckbox id="patio" setValues={setValues} field={formValues.patio} label="Patio" />
						<hr />
						<div className="space20" />
						<FormSelectBox
							id="services"
							setValues={setValues}
							multiple={true}
							field={formValues.services}
							label="Services"
							options={BusinessServiceLookup}
						/>
						<FormSelectBox
							id="delivery"
							setValues={setValues}
							multiple={true}
							field={formValues.delivery}
							label="Delivery Options"
							options={DeliveryType}
						/>
						<FormInput
							id="otherDelivery"
							setValues={setValues}
							field={formValues.otherDelivery}
							label="Other Delivery methods"
						/>
					</>
				)}
			</div>
		</>
	);
};
